import { useState, useEffect, useRef } from "react"
import {
  Box,
  Paper,
  TextField,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Toolbar,
  Divider,
  IconButton,
  Tooltip,
  Autocomplete,
  TextField as MuiTextField,
  Popper,
  Button,
  Menu,
  MenuItem
} from "@mui/material"
import {
  Visibility,
  Edit,
  ViewColumn,
  ContentCopy,
  Download,
  Upload,
  FormatBold,
  FormatItalic,
  FormatQuote,
  FormatListBulleted,
  FormatListNumbered,
  AddPhotoAlternate,
  Link as LinkIcon,
  CheckBox as CheckBoxIcon,
  Code as CodeIcon,
  TableChart,
  Add as AddIcon,
  Close as CloseIcon
} from "@mui/icons-material"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { marked } from "marked"
import DOMPurify from "dompurify"
import MarkdownPreview from "./markdownPreview" // ajusta la ruta según tu estructura

const MarkdownEditor = ({
  initialValue = "",
  onChange,
  onClickFunction,
  templates = [],
  uploadOption = false,
  downloadOption = false,
  linkOption = false,
  imageOption = false,
  showEditOptions = false,
  templateOption = false
}) => {
  const [markdown, setMarkdown] = useState(initialValue);
  const [viewMode, setViewMode] = useState(showEditOptions ? 'split' : 'edit');
  const [htmlContent, setHtmlContent] = useState("");
  const [showTemplates, setShowTemplates] = useState(false);
  const textareaRef = useRef(null);

  useEffect(() => {
    setMarkdown(initialValue)
  }, [initialValue])

  useEffect(() => {
    marked.setOptions({
      gfm: true,
      breaks: true,
      headerIds: false,
      mangle: false,
      smartLists: true, // esta opción ayuda con listas numeradas largas
    })
  }, [])

  useEffect(() => {
    const convertMarkdown = async () => {
      try {
        const rawHtml = await marked(markdown);
        const cleanHtml = DOMPurify.sanitize(rawHtml);
        setHtmlContent(cleanHtml);
      } catch (error) {
        console.error("Error converting markdown:", error)
        setHtmlContent("<p>Error al procesar el markdown</p>")
      }
    }

    convertMarkdown()
  }, [markdown])

  const [anchorEl, setAnchorEl] = useState(null);
  const isHeaderMenuOpen = Boolean(anchorEl);

  const handleOpenHeaderMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseHeaderMenu = () => {
    setAnchorEl(null);
  };

  const handleInsertHeader = (level) => {
    insertAtCursor(`${"#".repeat(level)} %s`);
    handleCloseHeaderMenu();
  };

  const insertAtCursor = (format) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = markdown.slice(start, end);

    // Si hay un %s en el formato, se reemplaza con el texto seleccionado
    const formattedText = format.includes('%s')
      ? format.replace('%s', selectedText || 'texto')
      : format;

    const newMarkdown =
      markdown.substring(0, start) +
      formattedText +
      markdown.substring(end);

    setMarkdown(newMarkdown);
    if (onChange) onChange(newMarkdown);

    // Reubicar el cursor después del texto insertado
    setTimeout(() => {
      const cursorPos = start + formattedText.length;
      textarea.focus();
      textarea.setSelectionRange(cursorPos, cursorPos);
    }, 0);
  };


  const handleTemplateSelect = (event, selected) => {
    if (selected?.descripcion) {
      insertAtCursor(selected.descripcion)
      setShowTemplates(false)
    }
  }

  const handleMarkdownChange = (event) => {
    const newValue = event.target.value
    setMarkdown(newValue)
    if (onChange) {
      onChange(newValue);
    }
  }

  const handleViewModeChange = (event, newMode) => {
    if (newMode !== null) {
      setViewMode(newMode)
    }
  }

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(markdown)
    } catch (err) {
      console.error("Error al copiar:", err)
    }
  }

  const downloadMarkdown = () => {
    const element = document.createElement("a")
    const file = new Blob([markdown], { type: "text/markdown" })
    element.href = URL.createObjectURL(file)
    element.download = "documento.md"
    document.body.appendChild(element)
    element.click()
    document.body.removeChild(element)
  }

  const uploadFile = (event) => {
    const file = event.target.files[0]
    if (file && file.type === "text/markdown") {
      const reader = new FileReader()
      reader.onload = (e) => {
        const content = e.target.result
        setMarkdown(content)
        if (onChange) {
          onChange(content);
          setMarkdown('')
        }
      }
      reader.readAsText(file)
    }
  }

  const renderEditor = () => (
    <TextField
      inputRef={textareaRef}
      multiline
      fullWidth
      variant="outlined"
      placeholder="Escribe tu markdown aquí..."
      value={markdown}
      onChange={handleMarkdownChange}
      minRows={10}
      maxRows={20}        // Limita el máximo de filas visibles y habilita scroll si se pasa
      sx={{
        fontFamily: "monospace",
        fontSize: "14px",
        lineHeight: 1.5,
        whiteSpace: "pre-wrap",   // Asegura que los saltos de línea se respeten
        overflowY: "auto",        // Muestra scroll vertical si es necesario
        "& .MuiOutlinedInput-input": {
          padding: "12px",
          whiteSpace: "pre-wrap", // También aquí para el input interno
        },
      }}
      InputProps={{
        style: {
          alignItems: "flex-start",
        },
      }}
    />
  )

  return (
    <Paper elevation={2} sx={{ height: "100%", display: "flex", flexDirection: "column", marginTop: '10px' }}>
      <Toolbar sx={{ borderBottom: 1, borderColor: "divider", flexWrap: "wrap", gap: 1 }}>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center",               "& > *:not(:last-child)": {
                marginRight: 1,
              }, }}>
          {/* Botones de formato */}
          <Tooltip title="Insertar encabezado">
            <Button
              variant="outlined"
              size="small"
              onClick={handleOpenHeaderMenu}
              endIcon={<ExpandMoreIcon />}
              sx={{ textTransform: "none", borderColor: '#148f9f', color: '#148f9f' }}
            >
              Encabezado
            </Button>
          </Tooltip>

          <Menu
            anchorEl={anchorEl}
            open={isHeaderMenuOpen}
            onClose={handleCloseHeaderMenu}
          >
            <MenuItem onClick={() => handleInsertHeader(1)}>H1</MenuItem>
            <MenuItem onClick={() => handleInsertHeader(2)}>H2</MenuItem>
            <MenuItem onClick={() => handleInsertHeader(3)}>H3</MenuItem>
            <MenuItem onClick={() => handleInsertHeader(4)}>H4</MenuItem>
          </Menu>
          <Tooltip title="Negrita">
            <IconButton onClick={() => insertAtCursor("**%s**")}>
              <FormatBold fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Cursiva">
            <IconButton onClick={() => insertAtCursor("_%s_")}>
              <FormatItalic fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Cita">
            <IconButton onClick={() => insertAtCursor("> %s")}>
              <FormatQuote fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Lista">
            <IconButton onClick={() => insertAtCursor("- %s")}>
              <FormatListBulleted fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Numerada">
            <IconButton onClick={() => insertAtCursor("1. %s")}>
              <FormatListNumbered fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Código">
            <IconButton onClick={() => insertAtCursor("`%s`")}>
              <CodeIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {
            linkOption ? (
              <Tooltip title="Link">
                <IconButton onClick={() => insertAtCursor("[texto](https://)")}>
                  <LinkIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : null
          }
          {
            imageOption ? (
              <Tooltip title="Imagen">
                <IconButton onClick={() => insertAtCursor("![alt](https://)")}>
                  <AddPhotoAlternate fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : null
          }
          <Tooltip title="Tabla">
            <IconButton
              onClick={() =>
                insertAtCursor(
                  "| Encabezado 1 | Encabezado 2 |\n|--------------|--------------|\n| Celda 1      | Celda 2      |"
                )
              }
            >
              <TableChart fontSize="small" />
            </IconButton>
          </Tooltip>

          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

          {/* Funciones */}
          <Tooltip title="Copiar markdown">
            <IconButton onClick={copyToClipboard}>
              <ContentCopy fontSize="small" />
            </IconButton>
          </Tooltip>
          {
            downloadOption ? (
            <Tooltip title="Descargar archivo">
              <IconButton onClick={downloadMarkdown}>
                <Download fontSize="small" />
              </IconButton>
            </Tooltip>
            ) : null
          }
          {
            uploadOption ? (
            <Tooltip title="Cargar archivo">
              <IconButton component="label">
                <Upload fontSize="small" />
                <input type="file" accept=".md,.markdown" onChange={uploadFile} style={{ display: "none" }} />
              </IconButton>
            </Tooltip>
            ) : null
          }

          {
            (!showTemplates && showEditOptions) ? (
              <ToggleButtonGroup value={viewMode} exclusive onChange={handleViewModeChange} size="small">
                <ToggleButton value="edit">
                  <Edit fontSize="small" />
                </ToggleButton>
                <ToggleButton value="split">
                  <ViewColumn fontSize="small" />
                </ToggleButton>
                <ToggleButton value="preview">
                  <Visibility fontSize="small" />
                </ToggleButton>
              </ToggleButtonGroup>
            ) : null
          }
          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
          {/* Templates */}
          {
            templateOption ? (
              <Tooltip title="Ver listado de plantilla de recetas">
                {
                  showTemplates ? (
                      <IconButton
                        sx={{
                          borderColor: '#148f9f',
                          color: '#148f9f',
                          height: 36,
                          minHeight: 36,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}

                        variant="outlined"
                        onClick={() => setShowTemplates(!showTemplates)}>
                        <CloseIcon />
                    </IconButton>
                  ) : (
                  <Button
                    sx={{
                      borderColor: '#148f9f',
                      color: '#148f9f',
                      height: 36,
                      minHeight: 36,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}

                    variant="outlined"
                    onClick={() => setShowTemplates(!showTemplates)}>
                    Plantillas
                </Button>
                  )
                }
              </Tooltip>
            ) : null
          }

          {showTemplates && (
            <Autocomplete
              disableClearable
              size="small"
              openOnFocus
              options={templates}
              getOptionLabel={(option) => option.nombre }
              onChange={handleTemplateSelect}
              sx={{ width: 200 }}
              renderInput={(params) => <MuiTextField {...params} label="Templates" />}
              PopperComponent={(props) => <Popper {...props} style={{ zIndex: 1300 }} />}
            />
          )}
          {
            templateOption && !showTemplates ? (
              <Tooltip title="Agregar nueva plantilla">
                <Button
                  sx={{
                    borderColor: '#148f9f',
                    color: '#148f9f',
                    height: 36,
                    minHeight: 36,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}

                  variant="outlined"
                  onClick={onClickFunction}>
                  <AddIcon fontSize="small" />
                </Button>
              </Tooltip>
            ) : null
          }
        </Box>
      </Toolbar>

      {/* Area de contenido */}
      <Box sx={{ flex: 1, display: "flex", overflow: "hidden", height: "100%" }}>
        {viewMode === "edit" && <Box sx={{ flex: 1, p: 1 }}>{renderEditor()}</Box>}
        {viewMode === "preview" &&
          <Box sx={{ flex: 1, p: 1 }}>
          <MarkdownPreview markdown={markdown} />
          </Box>}
          {viewMode === "split" && (
            <>
              <Box
                sx={{
                  width: "50%",
                  p: 1,
                  borderRight: 1,
                  borderColor: "divider",
                  overflow: "auto"
                }}
              >
                {renderEditor()}
              </Box>
              <Box
                sx={{
                  width: "50%",
                  p: 1,
                  overflow: "auto",
                  wordBreak: "break-word", // rompe texto largo sin espacios
                  whiteSpace: "pre-wrap"   // respeta saltos de línea
                }}
              >
                <MarkdownPreview markdown={markdown} showPreview={true} />
              </Box>
            </>
          )}

      </Box>
    </Paper>
  )
}

export default MarkdownEditor
